import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  changePaymentMethodDiscountStatus,
  createPaymentMethodDiscount,
  deletePaymentMethodDiscount,
  getPaymentMethodDiscountById,
  getPaymentMethodDiscounts,
  updatePaymentMethodDiscount,
} from "../services/paymentMethodDiscountService";
import {
  CreatePaymentMethodDiscountRequest,
  PaymentMethodDiscount,
  UpdatePaymentMethodDiscountRequest,
} from "../types/admin";
import { ApiResponse } from "../types/common";

export const paymentMethodDiscountKeys = {
  all: ["payment-method-discounts"] as const,
  list: () => [...paymentMethodDiscountKeys.all, "list"] as const,
  detail: (id: string) =>
    [...paymentMethodDiscountKeys.all, "detail", id] as const,
};

export const usePaymentMethodDiscounts = () => {
  return useQuery<ApiResponse<PaymentMethodDiscount[]>>({
    queryKey: paymentMethodDiscountKeys.list(),
    queryFn: () => getPaymentMethodDiscounts(),
    staleTime: 30 * 1000,
  });
};

export const usePaymentMethodDiscount = (id: string) => {
  return useQuery<ApiResponse<PaymentMethodDiscount>>({
    queryKey: paymentMethodDiscountKeys.detail(id),
    queryFn: () => getPaymentMethodDiscountById(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};

export const usePaymentMethodDiscountMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: paymentMethodDiscountKeys.list(),
    });
  };

  const create = useMutation({
    mutationFn: (data: CreatePaymentMethodDiscountRequest) =>
      createPaymentMethodDiscount(data),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdatePaymentMethodDiscountRequest;
    }) => updatePaymentMethodDiscount(id, data),
    onSuccess: (_, variables) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: paymentMethodDiscountKeys.detail(variables.id),
      });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePaymentMethodDiscount(id),
    onSuccess: invalidate,
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      changePaymentMethodDiscountStatus(id, isActive),
    onSuccess: (_, variables) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: paymentMethodDiscountKeys.detail(variables.id),
      });
    },
  });

  return { create, update, remove, changeStatus };
};

